// Capture helpers — turn a dropped/pasted image into the base64 payload the
// backend's POST /photos expects (raw base64, no `data:` prefix).

/** Read a File/Blob (a dropped or pasted image) into raw base64. */
export function fileToBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image.'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected image encoding.'));
        return;
      }
      // result is "data:<mime>;base64,<payload>" — strip the prefix.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/** Pull the first image File out of a clipboard/drag DataTransfer, if any. */
export function firstImageFile(data: DataTransfer | null | undefined): File | null {
  if (!data) return null;
  // Files first (drag-drop, paste of a saved screenshot).
  for (const f of Array.from(data.files)) {
    if (f.type.startsWith('image/')) return f;
  }
  // Then clipboard items (a screenshot copied straight to the clipboard).
  for (const item of Array.from(data.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) return f;
    }
  }
  return null;
}

/** Detect a single URL pasted as plain text, for link-unfurl capture. */
const URL_RE = /^https?:\/\/[^\s]+$/i;
export function asPastedLink(text: string): string | null {
  const t = text.trim();
  return URL_RE.test(t) ? t : null;
}
